import {
  type ActionFromReducersMapObject,
  combineReducers,
  configureStore,
  type PayloadAction,
  type StateFromReducersMapObject,
} from '@reduxjs/toolkit'
import { useDispatch, useSelector } from 'react-redux'

import { tmsApi } from '#api/tmsApi'
import { tmsApiService } from '#api/tmsApiService'
import { apiRequestPerformanceObserver } from '#features/DebugSettings/RequestMonitor/ApiRequestPerformanceObserver'
import { componentConfigurationActions } from '#store/componentConfiguration'
import { debugActions } from '#store/debugConfiguration'
import { factoryCodesSlice } from '#store/factoryCodesSlice'
import { layoutConfigurationActions } from '#store/layoutConfiguration'
import { marketsActions, marketsSlice } from '#store/marketsSlice'
import { factoryCodesObserver } from '#store/middleware/factoryCodesMiddleware'
import { marketsObserver } from '#store/middleware/marketsMiddleware'
import { nationalCodesObserver } from '#store/middleware/nationalCodesMiddleware'
import { referenceTrucksObserver } from '#store/middleware/referenceTrucksMiddleware'
import { nationalCodesActions, nationalCodesSlice } from '#store/nationalCodesSlice'
import { referenceTrucksActions, referenceTrucksSlice } from '#store/referenceTrucksSlice'
import { sharedSlices } from '#store/sharedBaseStore'
import { makeAppLocalStorage } from './middleware/appLocalStorage'

const reducers = {
  [marketsSlice.reducerPath]: marketsSlice.reducer,
  [nationalCodesSlice.reducerPath]: nationalCodesSlice.reducer,
  [factoryCodesSlice.reducerPath]: factoryCodesSlice.reducer,
  [referenceTrucksSlice.reducerPath]: referenceTrucksSlice.reducer,
  [tmsApiService.reducerPath]: tmsApiService.reducer,
  ...sharedSlices,
}
const rootReducer = combineReducers(reducers)

const allActions = {
  ...marketsActions,
  ...nationalCodesActions,
  ...componentConfigurationActions,
  ...debugActions,
  ...layoutConfigurationActions,
  ...referenceTrucksActions,
}

export type RootState = StateFromReducersMapObject<typeof reducers>
export type RootActions = ActionFromReducersMapObject<typeof reducers>
export type RootStore = typeof store

const hookedReducer = (state: RootState | Partial<RootState> | undefined, action: RootActions): RootState => {
  // special kind of reducer that allows for fully replacing the entire state
  if (action.type === '__system/replace') {
    const message = action as PayloadAction<RootState>
    return message.payload
  }
  return rootReducer(state, action)
}

// biome-ignore lint/style/noNonNullAssertion: unfortunate and poor initialization logic forces this pattern right now
export let store: Awaited<ReturnType<typeof lateInitializeStore>> = undefined!

export const lateInitializeStore = async () => {
  const appStorage = await makeAppLocalStorage()
  const preloadStore = configureStore({
    preloadedState: rootReducer(undefined, { type: '@@INIT' }),
    reducer: rootReducer,
    devTools: {
      name: 'MAIC Admin Frontend',
      actionCreators: allActions,
      actionsDenylist: [
        // all of redux-query actions are ignored (extremly noisy, use redux devtools with RTK Query at the bottom of the tool)
        ...Object.values(tmsApi.internalActions).map((x) => x.type),
        `${tmsApi.reducerPath}/executeQuery`,
        `${tmsApi.reducerPath}/executeQuery/fulfilled`,
        `${tmsApi.reducerPath}/executeMutation/pending`,
        `${tmsApi.reducerPath}/executeMutation/fulfilled`,
      ],
    },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware()
        .prepend(nationalCodesObserver.middleware)
        .prepend(factoryCodesObserver.middleware)
        .prepend(marketsObserver.middleware)
        .prepend(referenceTrucksObserver.middleware)
        .concat(tmsApi.middleware)
        .concat(appStorage.localStorageListener.middleware),
  })

  const state = await appStorage.loadStoredState(hookedReducer(undefined, { type: '@@INIT' }))
  apiRequestPerformanceObserver.connectStore(store)

  preloadStore.dispatch({
    type: '__system/replace',
    payload: state,
  })

  store = preloadStore
  return preloadStore
}

export type AppDispatch = typeof store.dispatch

export const useAppDispatch = useDispatch.withTypes<AppDispatch>()
export const useAppSelector = useSelector.withTypes<RootState>()
